/**
 * 單據附件的前端 API(REQ-20260907002)。
 *
 * 端點是**跨單據共用**的 `/document-attachments/:docType/:docId`,所以這一份
 * 放在自己的 feature 資料夾而不是 `features/accounting/api.ts` ——
 * 之後出貨單 / 報價單要貼檔案時,呼叫的是同樣這幾支。
 *
 * 上傳流程與既有的進貨單附件逐項相同(presign → 直傳 MinIO → 回寫 row),
 * 兩處不同:檔案類型放寬成 `image/*` + PDF(共用
 * `isAllowedDocumentAttachmentMime()`,不各寫一份 if),以及多一支 PATCH 改分類。
 */
import { useQuery } from '@tanstack/react-query';
import {
  DOCUMENT_ATTACHMENT_MAX_BYTES,
  isAllowedDocumentAttachmentMime,
  type ApiSuccess,
  type DocumentAttachmentCategory,
  type DocumentAttachmentResponse,
  type DocumentType,
  type PresignDocumentUploadResponse,
  type AttachmentRotation,
} from '@app/shared';
import axios from 'axios';
import { apiClient } from '../../lib/api-client';

function basePath(docType: DocumentType, docId: number): string {
  return `/document-attachments/${docType}/${docId}`;
}

export function documentAttachmentsKey(docType: DocumentType, docId: number | undefined): unknown[] {
  return ['document-attachments', docType, docId ?? 0];
}

/**
 * 清單。`docId` 還沒有(建單模式下單還不存在)時 `enabled: false` ——
 * 一次請求都不發,卡片仍然渲染得出來(空清單 + 停用的上傳鈕)。
 */
export function useDocumentAttachments(
  docType: DocumentType,
  docId: number | undefined,
): ReturnType<typeof useQuery<DocumentAttachmentResponse[]>> {
  return useQuery<DocumentAttachmentResponse[]>({
    queryKey: documentAttachmentsKey(docType, docId),
    enabled: docId != null,
    queryFn: async () => {
      const res = await apiClient.get<ApiSuccess<DocumentAttachmentResponse[]>>(
        basePath(docType, docId as number),
      );
      return res.data.data;
    },
  });
}

/**
 * 上傳。檔案類型 / 大小前端先擋一次(後端仍會再擋一次,這裡只是為了立刻給訊息)。
 * `category` 選填 —— 需求原文明確要求「上傳當下先存檔,類型可以後補」。
 */
export async function uploadDocumentAttachment(
  docType: DocumentType,
  docId: number,
  file: File,
  category?: DocumentAttachmentCategory | null,
): Promise<DocumentAttachmentResponse> {
  if (!isAllowedDocumentAttachmentMime(file.type)) {
    throw new Error(`不支援的檔案類型:${file.type || '未知'}(限圖片或 PDF)`);
  }
  if (file.size > DOCUMENT_ATTACHMENT_MAX_BYTES) {
    throw new Error(`檔案過大,上限 ${DOCUMENT_ATTACHMENT_MAX_BYTES / 1024 / 1024} MB`);
  }

  const presign = await apiClient.post<ApiSuccess<PresignDocumentUploadResponse>>(
    `${basePath(docType, docId)}/presign`,
    { originalName: file.name, mimeType: file.type, sizeBytes: file.size },
  );
  const { uploadUrl, storedName, storagePath } = presign.data.data;

  await axios.put(uploadUrl, file, { headers: { 'Content-Type': file.type } });

  const created = await apiClient.post<ApiSuccess<DocumentAttachmentResponse>>(
    basePath(docType, docId),
    {
      originalName: file.name,
      mimeType: file.type,
      sizeBytes: file.size,
      storedName,
      storagePath,
      category: category ?? null,
    },
  );
  return created.data.data;
}

/** 只改分類。`null` = 清回未分類 */
export async function patchDocumentAttachmentCategory(
  docType: DocumentType,
  docId: number,
  attachmentId: number,
  category: DocumentAttachmentCategory | null,
): Promise<DocumentAttachmentResponse> {
  const res = await apiClient.patch<ApiSuccess<DocumentAttachmentResponse>>(
    `${basePath(docType, docId)}/${attachmentId}`,
    { category },
  );
  return res.data.data;
}

/**
 * 只改檢視角度(REQ-20260909004)—— 走另一支端點,不是上面那支改分類的 PATCH
 * (那支的 `category` 是必填,借用會逼著只想轉方向的請求一併宣告分類)。
 * ⚠️ 只寫 DB 的 `rotation` 欄位,MinIO 上的原圖與 EXIF 完全不動。
 */
export async function saveDocumentAttachmentRotation(
  docType: DocumentType,
  docId: number,
  attachmentId: number,
  rotation: AttachmentRotation,
): Promise<DocumentAttachmentResponse> {
  const res = await apiClient.patch<ApiSuccess<DocumentAttachmentResponse>>(
    `${basePath(docType, docId)}/${attachmentId}/rotation`,
    { rotation },
  );
  return res.data.data;
}

export async function deleteDocumentAttachment(
  docType: DocumentType,
  docId: number,
  attachmentId: number,
): Promise<void> {
  await apiClient.delete(`${basePath(docType, docId)}/${attachmentId}`);
}

/**
 * 預覽 / 下載網址。後端 presign 時就帶了 `inline` 的 Content-Disposition,
 * 所以這個網址可以直接當 `<img src>`、餵給 pdf.js,或新分頁開啟。
 */
export async function getDocumentAttachmentUrl(
  docType: DocumentType,
  docId: number,
  attachmentId: number,
): Promise<string> {
  const res = await apiClient.get<ApiSuccess<{ url: string; originalName: string }>>(
    `${basePath(docType, docId)}/${attachmentId}/signed-url`,
  );
  return res.data.data.url;
}

export function useDocumentAttachmentUrl(
  docType: DocumentType,
  docId: number | undefined,
  attachmentId: number,
): ReturnType<typeof useQuery<string>> {
  return useQuery<string>({
    queryKey: ['document-attachment-url', docType, docId ?? 0, attachmentId],
    enabled: docId != null,
    // signed URL 5 分鐘過期,設 stale 4 分鐘(同既有的進貨單附件)
    staleTime: 1000 * 60 * 4,
    queryFn: () => getDocumentAttachmentUrl(docType, docId as number, attachmentId),
  });
}
